/**
 * Row-identity token (`_id`) helpers for array-of-object field values.
 *
 * `_id` is a client-only UUID. The backend ontology has no row-identity
 * concept for array-of-object fields — rows come back from the API as bare
 * objects with only domain properties — so "do we have an id from the
 * backend?" is always "no". Any `_id` you see in memory was stamped locally.
 *
 * Rows acquire a `_id` via one of two client-side paths:
 *   - Interactively: `ArrayTableSectionView` calls `v4()` when the user adds
 *     a new row, so rows authored in the UI carry a `_id` from the moment
 *     they exist.
 *   - Programmatically: `stampArrayRowIds` runs at ingestion points that seed
 *     `FieldValues` from sources which don't know about our row-identity
 *     convention. Currently that is the Gong Agent LLM autofill response —
 *     its rows arrive as plain objects and would otherwise reach the
 *     array-table view without a `_id`, causing React to churn keys on every
 *     render (killing text-input focus) and leaving comment anchors with
 *     nothing stable to reference. If a future ingest path (e.g. bulk import,
 *     template cloning) also produces rows without `_id`, it should pipe
 *     through `stampArrayRowIds` too.
 *
 * On subsequent user edits the already-stamped `_id` flows through unchanged,
 * so `hasId` short-circuits and the same wrapper reference is returned,
 * keeping downstream memoisation stable.
 *
 * `stripArrayRowIds` is the inverse, used by read-only consumers (e.g. the
 * LLM-vs-user diff drawer) that need to compare or display values without the
 * locally stamped token leaking in as spurious content. They form a logical
 * pair; keep them co-located so a change to one prompts thinking about the
 * other.
 *
 * The two functions intentionally use slightly different `_id` presence
 * predicates: stamping treats an empty-string `_id` as "needs a fresh UUID"
 * (`hasId` below), while stripping nukes any `_id` key regardless of value.
 * Do not DRY this up into a shared predicate.
 */

import type { FieldValues } from 'features/libra/ontology/ontologyFieldTypes'
import { v4 } from 'uuid'

/**
 * Normalises a `FieldValues` payload so that array-of-object rows carry a
 * stable `_id`. Only rows that are plain objects lacking a truthy `_id` are
 * touched; everything else passes through unchanged, and when an entry does
 * not need changes the original wrapper reference is preserved so downstream
 * memoisation stays stable.
 *
 * Expects its input to already conform to `FieldValues`; untrusted sources
 * (raw network payloads) should pass through `FieldValuesSchema.parse` first.
 * Row-level discrimination inside each wrapper (`Array.isArray`, `isPlainRow`,
 * `hasId`) is kept here because `FieldValue.value` is intentionally `unknown` —
 * different ontology field types use different value shapes.
 */
export function stampArrayRowIds(fv: FieldValues): FieldValues {
  let changed = false
  const out: FieldValues = {}

  for (const [fieldId, wrapper] of Object.entries(fv)) {
    if (!Array.isArray(wrapper.value)) {
      out[fieldId] = wrapper
      continue
    }

    let rowChanged = false
    const nextRows = wrapper.value.map((row) => {
      if (!isPlainRow(row)) return row
      if (hasId(row)) return row
      rowChanged = true
      return { ...row, _id: v4() }
    })

    if (!rowChanged) {
      out[fieldId] = wrapper
      continue
    }

    changed = true
    out[fieldId] = { ...wrapper, value: nextRows }
  }

  return changed ? out : fv
}

/**
 * Inverse of `stampArrayRowIds` for read-only consumers that need to compare
 * or display values without the locally stamped `_id` leaking in as spurious
 * content.
 *
 * Only plain-object rows inside a top-level array are touched — mirroring the
 * shape stamping applies to. Primitives, non-arrays, and arrays of primitives
 * pass through by reference so downstream memoisation stays stable. When no
 * row carries an `_id`, the original reference is returned.
 *
 * Operates on raw inner values, not on the `FieldValue` wrapper, because the
 * diff drawer needs to strip both `FieldValue.value` and `FieldValue.llmValue`
 * independently.
 */
export function stripArrayRowIds<T>(value: T): T {
  if (!Array.isArray(value)) return value

  let changed = false
  const next = value.map((row) => {
    if (!isPlainRow(row)) return row
    if (!('_id' in row)) return row
    changed = true
    const rest = { ...row }
    delete rest._id
    return rest
  })

  return changed ? (next as unknown as T) : value
}

function isPlainRow(row: unknown): row is Record<string, unknown> {
  return row !== null && typeof row === 'object' && !Array.isArray(row)
}

function hasId(row: Record<string, unknown>): boolean {
  return typeof row._id === 'string' && row._id.length > 0
}
